import React, { useMemo, useState } from "react";
import { Button, Spin } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, DownloadOutlined, HolderOutlined } from "@ant-design/icons";
import { reorderListItems } from "../../../app/utils/common/reorderList";
import CompanyPolicyPdfViewer from "../CompanyPolicyPdfViewer/CompanyPolicyPdfViewer";
import ProjectDocumentationDocxViewer from "../ProjectDocumentationDocxViewer/ProjectDocumentationDocxViewer";
import ProjectDocumentationExcelViewer from "../ProjectDocumentationExcelViewer/ProjectDocumentationExcelViewer";
import ProjectDocumentationMarkdownViewer from "../ProjectDocumentationMarkdownViewer/ProjectDocumentationMarkdownViewer";
import ProjectDocumentationHtmlViewer from "../ProjectDocumentationHtmlViewer/ProjectDocumentationHtmlViewer";
import {
  fetchProjectDocumentationFileBlob,
  isProjectDocDocx,
  isProjectDocExcel,
  isProjectDocHtml,
  isProjectDocMarkdown,
  isProjectDocPdf,
  isProjectDocPlainText,
  isProjectDocJson,
} from "../../../app/utils/common/projectDocumentationFile";
import ProjectDocumentationTextViewer from "../ProjectDocumentationTextViewer/ProjectDocumentationTextViewer";
import "./ProjectDocumentationBundledFilesView.scss";

const DRAG_FILE_INDEX = "application/x-project-doc-file-index";

const FilePreviewPane = ({ documentId, file, onLoadError }) => {
  if (!file) return null;

  if (isProjectDocExcel(file)) {
    return (
      <ProjectDocumentationExcelViewer
        documentId={documentId}
        fileDocumentId={file.id}
        onLoadError={onLoadError}
      />
    );
  }
  if (isProjectDocPdf(file)) {
    return <BundledPdfPreview documentId={documentId} file={file} onLoadError={onLoadError} />;
  }
  if (isProjectDocDocx(file)) {
    return (
      <ProjectDocumentationDocxViewer
        documentId={documentId}
        fileDocumentId={file.id}
        onLoadError={onLoadError}
      />
    );
  }
  if (isProjectDocMarkdown(file)) {
    return (
      <ProjectDocumentationMarkdownViewer
        documentId={documentId}
        fileDocumentId={file.id}
        onLoadError={onLoadError}
      />
    );
  }
  if (isProjectDocHtml(file)) {
    return (
      <ProjectDocumentationHtmlViewer
        documentId={documentId}
        fileDocumentId={file.id}
        onLoadError={onLoadError}
      />
    );
  }
  if (isProjectDocPlainText(file) || isProjectDocJson(file)) {
    return (
      <ProjectDocumentationTextViewer
        documentId={documentId}
        fileDocumentId={file.id}
        onLoadError={onLoadError}
        formatJson={isProjectDocJson(file)}
      />
    );
  }

  return <p className="text-gray-500 text-sm m-0">Preview is not available for this file type.</p>;
};

const BundledPdfPreview = ({ documentId, file, onLoadError }) => {
  const [url, setUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    setLoading(true);
    setUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(documentId, file.id);
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(blobUrl);
      } catch (e) {
        if (!cancelled) onLoadError?.(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [documentId, file?.id, onLoadError]);

  if (loading) return <p className="text-gray-500 text-sm">Loading PDF…</p>;
  if (!url) return <p className="text-gray-500 text-sm">Unable to load PDF.</p>;

  return (
    <div
      className="project-doc-bundled-files__pdf-wrap"
      onDragStart={(e) => e.preventDefault()}
    >
      <CompanyPolicyPdfViewer fileUrl={url} onLoadError={onLoadError} />
    </div>
  );
};

const ProjectDocumentationBundledFilesView = ({
  documentId,
  files = [],
  onDownloadFile,
  downloadingFileId,
  onLoadError,
  canReorder = false,
  onReorderFiles,
  isReordering = false,
}) => {
  const safeFiles = useMemo(() => (Array.isArray(files) ? files.filter((f) => f?.id) : []), [files]);
  const [activeFileId, setActiveFileId] = useState(() => safeFiles[0]?.id ?? null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const activeFile =
    safeFiles.find((f) => Number(f.id) === Number(activeFileId)) || safeFiles[0] || null;

  const activeIndex = safeFiles.findIndex((f) => Number(f.id) === Number(activeFile?.id));

  const applyReorder = (fromIndex, toIndex) => {
    if (isReordering || !onReorderFiles || fromIndex === toIndex) return;
    const reordered = reorderListItems(safeFiles, fromIndex, toIndex);
    onReorderFiles(documentId, reordered);
  };

  const handleMove = (direction) => {
    if (!activeFile || activeIndex < 0) return;
    const targetIndex = direction === "left" ? activeIndex - 1 : activeIndex + 1;
    if (targetIndex < 0 || targetIndex >= safeFiles.length) return;
    applyReorder(activeIndex, targetIndex);
  };

  const handleDragStart = (e, index) => {
    if (!canReorder || isReordering) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DRAG_FILE_INDEX, String(index));
    setDragFromIndex(index);
  };

  const handleDragEnd = () => {
    setDragFromIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    if (!canReorder || isReordering || dragFromIndex == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    if (!canReorder || isReordering) return;
    e.preventDefault();
    const fromIndex = Number(e.dataTransfer.getData(DRAG_FILE_INDEX));
    handleDragEnd();
    if (!Number.isFinite(fromIndex)) return;
    applyReorder(fromIndex, dropIndex);
  };

  if (!safeFiles.length) {
    return <p className="text-gray-500">No files in this document.</p>;
  }

  const reorderDisabled = isReordering;

  return (
    <div className="project-doc-bundled-files h-full flex flex-col min-h-0 relative">
      {isReordering ? (
        <div
          className="project-doc-bundled-files__reorder-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Spin size="large" />
          <p className="project-doc-bundled-files__reorder-overlay-text">Updating file order…</p>
        </div>
      ) : null}
      <div className="project-doc-bundled-files__toolbar flex flex-wrap items-center justify-between gap-2 mb-3 shrink-0">
        <p className="text-sm font-semibold text-[#1f2b44] m-0">
          {safeFiles.length} file{safeFiles.length === 1 ? "" : "s"}
          {canReorder ? (
            <span className="font-normal text-gray-500 ml-2">
              Drag a file tab onto another position to reorder
            </span>
          ) : null}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {canReorder && onReorderFiles ? (
            <>
              <Button
                icon={<ArrowLeftOutlined />}
                disabled={reorderDisabled || activeIndex <= 0}
                loading={isReordering}
                onClick={() => handleMove("left")}
                title="Move selected file one position left"
              >
                Move left
              </Button>
              <Button
                icon={<ArrowRightOutlined />}
                disabled={reorderDisabled || activeIndex < 0 || activeIndex >= safeFiles.length - 1}
                loading={isReordering}
                onClick={() => handleMove("right")}
                title="Move selected file one position right"
              >
                Move right
              </Button>
            </>
          ) : null}
          {activeFile && onDownloadFile ? (
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingFileId === activeFile.id}
              onClick={() => onDownloadFile(documentId, activeFile)}
            >
              Download selected
            </Button>
          ) : null}
        </div>
      </div>
      <div className="project-doc-bundled-files__tabs flex flex-wrap gap-2 mb-3 shrink-0">
        {safeFiles.map((file, index) => {
          const isActive = Number(file.id) === Number(activeFile?.id);
          const isDragging = dragFromIndex === index;
          const isDragOver = dragOverIndex === index && dragFromIndex !== index;
          return (
            <button
              key={file.id}
              type="button"
              draggable={canReorder && safeFiles.length > 1 && !reorderDisabled}
              disabled={reorderDisabled}
              className={`project-doc-bundled-files__tab ${isActive ? "project-doc-bundled-files__tab--active" : ""} ${isDragging ? "project-doc-bundled-files__tab--dragging" : ""} ${isDragOver ? "project-doc-bundled-files__tab--drag-over" : ""} ${reorderDisabled ? "project-doc-bundled-files__tab--disabled" : ""}`}
              onClick={() => {
                if (reorderDisabled) return;
                setActiveFileId(file.id);
              }}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              title={
                canReorder
                  ? `${file.fileName || ""} — drag onto another tab to move`
                  : file.fileName
              }
            >
              {canReorder ? (
                <HolderOutlined className="project-doc-bundled-files__drag-handle" aria-hidden />
              ) : null}
              {file.fileName || `File ${file.id}`}
            </button>
          );
        })}
      </div>
      <div className="project-doc-bundled-files__preview flex-1 min-h-[calc(100vh-280px)] overflow-hidden rounded-[10px]">
        {activeFile ? (
          <FilePreviewPane
            key={activeFile.id}
            documentId={documentId}
            file={activeFile}
            onLoadError={onLoadError}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProjectDocumentationBundledFilesView;
